import { z } from "zod";

import {
  machineSchema,
  servingPresetSchema,
  targetProfileSchema,
} from "./catalog";
import { API_VERSION } from "./common";
import { machineDomainSchema } from "./machine-domain";
import { trimStringInput } from "./schema-utils";
import {
  targetProfileDescriptionSchema,
  targetProfileDomainSchema,
  targetProfileLinkSchema,
} from "./target-profile-domain";

export const machineWriteSchema = machineDomainSchema.strict();

export const machineResponseSchema = z.object({
  apiVersion: z.literal(API_VERSION),
  data: machineSchema,
});

export const deleteMachineResponseSchema = z.object({
  apiVersion: z.literal(API_VERSION),
  data: z.object({
    deleted: z.literal(true),
    slug: z.string(),
    impactedRecipeCount: z.number().int().nonnegative(),
  }),
});

const optionalLinkSchema = z.preprocess(
  (value) => (value === "" || value === null ? undefined : value),
  trimStringInput(targetProfileLinkSchema).optional(),
);

export const targetProfileWriteSchema = targetProfileDomainSchema
  .extend({
    description: trimStringInput(targetProfileDescriptionSchema).optional(),
    link: optionalLinkSchema,
  })
  .strict();

export const targetProfileResponseSchema = z.object({
  apiVersion: z.literal(API_VERSION),
  data: targetProfileSchema,
});

export const deleteTargetProfileResponseSchema = z.object({
  apiVersion: z.literal(API_VERSION),
  data: z.object({ deleted: z.literal(true), slug: z.string() }),
});

export type MachineWrite = z.input<typeof machineWriteSchema>;
export type ParsedMachineWrite = z.output<typeof machineWriteSchema>;
export type MachineResponse = z.infer<typeof machineResponseSchema>;
export type DeleteMachineResponse = z.infer<typeof deleteMachineResponseSchema>;
export type TargetProfileWrite = z.input<typeof targetProfileWriteSchema>;
export type ParsedTargetProfileWrite = z.output<
  typeof targetProfileWriteSchema
>;
export type TargetProfileResponse = z.infer<typeof targetProfileResponseSchema>;
export type DeleteTargetProfileResponse = z.infer<
  typeof deleteTargetProfileResponseSchema
>;

export const servingPresetWriteSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(1, "Enter a name for this preset")
      .max(40, "Keep the name to 40 characters or fewer"),
    description: z
      .string()
      .trim()
      .max(60, "Keep the description to 60 characters or fewer")
      .optional(),
    temperatureC: z.number().finite(),
  })
  .strict();
export const servingPresetResponseSchema = z.object({
  apiVersion: z.literal(API_VERSION),
  data: servingPresetSchema,
});
export const deleteServingPresetResponseSchema = z.object({
  apiVersion: z.literal(API_VERSION),
  data: z.object({ deleted: z.literal(true), slug: z.string() }),
});
export type ServingPresetWrite = z.input<typeof servingPresetWriteSchema>;
export type ParsedServingPresetWrite = z.output<
  typeof servingPresetWriteSchema
>;
export type ServingPresetResponse = z.infer<typeof servingPresetResponseSchema>;
export type DeleteServingPresetResponse = z.infer<
  typeof deleteServingPresetResponseSchema
>;
